import React, { useState } from 'react';

const SamplingGenerator = () => {
  const [samplingMethod, setSamplingMethod] = useState("random");
  
  // State for Random Sampling
  const [randomTotal, setRandomTotal] = useState('');
  const [randomSampleSize, setRandomSampleSize] = useState('');
  const [randomSeries, setRandomSeries] = useState('');
  
  // State for Monetary Unit Sampling
  const [monetarySampleSize, setMonetarySampleSize] = useState('');
  const [monetaryFigures, setMonetaryFigures] = useState('');
  
  // State for the sample result
  const [sampleResult, setSampleResult] = useState([]);

  // Random Sampling function:
  // Shuffles the population array and returns the first n items.
  const randomSampling = (population, n) => {
    const shuffled = [...population].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n);
  };

  // Monetary Unit Sampling function:
  // Calculates a sampling interval based on the total monetary value,
  // then selects items based on cumulative sums crossing random thresholds.
  const monetaryUnitSampling = (data, n) => {
    const totalValue = data.reduce((acc, val) => acc + val, 0);
    if (totalValue === 0) {
      alert("Total value is zero. Cannot perform monetary unit sampling.");
      return [];
    }
    const interval = totalValue / n;
    const randomStart = Math.random() * interval;
    const thresholds = Array.from({ length: n }, (_, i) => randomStart + i * interval);
    let cumulative = 0;
    let sampleItems = [];
    let index = 0;
    thresholds.forEach((threshold) => {
      while (index < data.length && cumulative + data[index] < threshold) {
        cumulative += data[index];
        index++;
      }
      if (index < data.length) {
        // Return the item index (1-based) and its value
        sampleItems.push({ item: index + 1, value: data[index] });
      }
    });
    return sampleItems;
  };

  // Handler for Random Sampling inputs
  const handleRandomSampling = () => {
    const totalCount = parseInt(randomTotal, 10);
    const sampleSize = parseInt(randomSampleSize, 10);
    if (isNaN(totalCount) || isNaN(sampleSize)) {
      alert("Please enter valid numbers for total population and sample size.");
      return;
    }
    // Parse the series by splitting on commas and trimming whitespace.
    const seriesArray = randomSeries.split(",").map(item => item.trim()).filter(item => item !== "");
    if (seriesArray.length !== totalCount) {
      alert(`The number of items in the series (${seriesArray.length}) does not match the total population count (${totalCount}).`);
      return;
    }
    if (sampleSize > totalCount) {
      alert("Sample size cannot be greater than the total population.");
      return;
    }
    const result = randomSampling(seriesArray, sampleSize);
    setSampleResult(result);
  };

  // Handler for Monetary Sampling inputs
  const handleMonetarySampling = () => {
    const sampleSize = parseInt(monetarySampleSize, 10);
    if (isNaN(sampleSize) || sampleSize <= 0) {
      alert("Please enter a valid sample size.");
      return;
    }
    // Parse figures as numbers from the comma-separated input.
    const figuresArray = monetaryFigures.split(",").map(item => parseFloat(item.trim())).filter(item => !isNaN(item));
    if (figuresArray.length === 0) {
      alert("Please enter valid monetary figures.");
      return;
    }
    if (sampleSize > figuresArray.length) {
      alert("Sample size cannot be greater than the number of figures provided.");
      return;
    }
    const result = monetaryUnitSampling(figuresArray, sampleSize);
    setSampleResult(result);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Sampling Generator</h1>
      
      {/* Sampling Method Selector */}
      <div className="mb-6">
        <span className="mr-4 font-medium">Select Sampling Method:</span>
        <label className="mr-4">
          <input 
            type="radio" 
            name="samplingMethod" 
            value="random" 
            checked={samplingMethod === "random"}
            onChange={(e) => setSamplingMethod(e.target.value)}
            className="mr-2"
          />
          Random Sampling
        </label>
        <label>
          <input 
            type="radio" 
            name="samplingMethod" 
            value="monetary" 
            checked={samplingMethod === "monetary"}
            onChange={(e) => setSamplingMethod(e.target.value)}
            className="mr-2"
          />
          Monetary Unit Sampling
        </label>
      </div>
      
      {/* Form for Random Sampling */}
      {samplingMethod === "random" && (
        <div className="mb-6 border p-4 rounded shadow">
          <h2 className="text-2xl font-semibold mb-4">Random Sampling Inputs</h2>
          <div className="mb-4">
            <label className="block font-medium mb-1">Total Population Count:</label>
            <input 
              type="number" 
              value={randomTotal}
              onChange={(e) => setRandomTotal(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              placeholder="e.g. 5"
            />
          </div>
          <div className="mb-4">
            <label className="block font-medium mb-1">Sample Size:</label>
            <input 
              type="number" 
              value={randomSampleSize}
              onChange={(e) => setRandomSampleSize(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              placeholder="e.g. 3"
            />
          </div>
          <div className="mb-4">
            <label className="block font-medium mb-1">Population Series (comma separated):</label>
            <textarea 
              value={randomSeries}
              onChange={(e) => setRandomSeries(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              placeholder="e.g. A, B, C, D, E"
              rows="3"
            />
          </div>
          <button 
            onClick={handleRandomSampling}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Generate Random Sample
          </button>
        </div>
      )}
      
      {/* Form for Monetary Unit Sampling */}
      {samplingMethod === "monetary" && (
        <div className="mb-6 border p-4 rounded shadow">
          <h2 className="text-2xl font-semibold mb-4">Monetary Unit Sampling Inputs</h2>
          <div className="mb-4">
            <label className="block font-medium mb-1">Figures (comma separated):</label>
            <textarea 
              value={monetaryFigures}
              onChange={(e) => setMonetaryFigures(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              placeholder="e.g. 10, 20, 30, 40, 50"
              rows="3"
            />
          </div>
          <div className="mb-4">
            <label className="block font-medium mb-1">Sample Size:</label>
            <input 
              type="number" 
              value={monetarySampleSize}
              onChange={(e) => setMonetarySampleSize(e.target.value)}
              className="border rounded px-3 py-2 w-full"
              placeholder="e.g. 3"
            />
          </div>
          <button 
            onClick={handleMonetarySampling}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Generate Monetary Sample
          </button>
        </div>
      )}
      
      {/* Display Sample Result */}
      {sampleResult.length > 0 && (
        <div className="border p-4 rounded shadow">
          <h2 className="text-2xl font-semibold mb-4">Sample Result</h2>
          <ul className="list-disc pl-5">
            {samplingMethod === "random" ? (
              sampleResult.map((item, index) => (
                <li key={index}>{item}</li>
              ))
            ) : (
              sampleResult.map((item, index) => (
                <li key={index}>
                  Item {item.item}: {item.value}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SamplingGenerator;
